import React, { useEffect, useState } from 'react';
import { KTSVG, SimpleAlert } from '../../../../../_mexablock/helpers';
import { Modal } from 'react-bootstrap-v5';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import {
  createDevolucion,
  downloadDevolucionReportPDF,
} from '../../../../service/inventario/devolucion';
import {
  useSelectZonasActivas,
  useSelectRazon,
  useList,
} from '../hooks/DevolucionHook';
import Select from 'react-select';

type Props = {
  show: boolean;
  handleClose: (updateGrid: boolean) => void;
};

type SelectOptions = {
  value: number;
  label: string;
  user?: string;
};

const ZonaSchema = Yup.object().shape({
  description: Yup.string()
    .max(500, 'Máximo 500 caracteres.')
    .required('Requerido.'),
  zone: Yup.number()
    .required('Este campo es requerido')
    .min(1, 'Seleccione una zona'),
  razon: Yup.number()
    .required('Este campo es requerido')
    .min(1, 'Seleccione una razón'),
});

const CreateModal: React.FC<Props> = ({ show, handleClose }) => {
  const [loading, setLoading] = useState(false);
  const [selectedZona, setSelectedZona] = useState<SelectOptions | null>(null);
  const [selectedRazon, setSelectedRazon] = useState<SelectOptions | null>(
    null
  );
  const [selectedRango, setSelectedRango] = useState<SelectOptions | null>(
    null
  );
  const [selectedUbicacion, setSelectedUbicacion] =
    useState<SelectOptions | null>(null);
  const [sortedPaquetes, setSortedPaquetes] = useState<any[]>([]);
  const [isAsc, setIsAsc] = useState(true);
  const [idPackage, setidPackage] = useState<number>(0);
  const { data: zonas } = useSelectZonasActivas(show);
  const { data: razon } = useSelectRazon(show);
  const { data: lista } = useList(idPackage);

  const rango = Array.from({ length: 20 }, (_, i) => {
    const value = (i + 1) * 5;
    return { value, label: `${value}` };
  });

  const [selectedRows, setSelectedRows] = useState<Set<number>>(new Set());
  const formZona = useFormik({
    initialValues: {
      zone: 0,
      razon: 0,
      description: '',
      ubicacion: 0,
      stock: [] as string[],
    },
    validationSchema: ZonaSchema,
    enableReinitialize: true,
    onSubmit: async (values, { setSubmitting, resetForm }) => {
      setLoading(true);
      const iccidsSeleccionados = Array.from(selectedRows).map(
        (i) => lista[i].iccid
      );
      if (iccidsSeleccionados.length === 0) {
        SimpleAlert(
          'Algunos datos no han sido agregados. Por favor, asegúrese de que toda la información solicitada haya sido capturada.',
          3000,
          'error'
        );
        setSubmitting(false);
        setLoading(false);
        return;
      }
      values.stock = iccidsSeleccionados;
      try {
        setLoading(true);
        const res = await createDevolucion(values);
        SimpleAlert(res.data.message, 3000, 'success');
        resetForm();
        resetFormState();
        downloadDevolucionReportPDF(res.data?.doc?.data?.id);
        handleClose(true);
      } catch (err: any) {
        const { message, details } = err.response.data;
        SimpleAlert(details?.[0]?.message || message, 3000, 'error');
      } finally {
        setLoading(false);
        setSubmitting(false);
      }
    },
  });

  const handleZonas = (selectedOption: any) => {
    const id = selectedOption.value;
    const user = selectedOption.user;
    setSelectedZona(selectedOption);
    formZona.setFieldValue('zone', id);
    setidPackage(id);
    formZona.setFieldValue('responsability_user', user);
    setSelectedRows(new Set());
  };

  const handleRazon = (selectedOption: any) => {
    const id = selectedOption.value;
    setSelectedRazon(selectedOption);
    formZona.setFieldValue('razon', id);
  };

  const handleRango = (selectedOption: any) => {
    if (!selectedOption) {
      setSelectedRango(null);
      setSelectedRows(new Set());
      return;
    }

    const cantidad = selectedOption.value;
    setSelectedRango(selectedOption);

    if (lista && lista.length > 0) {
      const nuevasFilas = new Set<number>();
      const limite = Math.min(cantidad, lista.length);
      for (let i = 0; i < limite; i++) nuevasFilas.add(i);
      setSelectedRows(nuevasFilas);
    }
  };

  const isAllSelected = !!lista?.length && selectedRows.size === lista.length;

  const toggleSelectAll = () => {
    if (isAllSelected) {
      setSelectedRows(new Set());
    } else {
      setSelectedRows(new Set(lista.map((_, index) => index)));
    }
  };

  const toggleSelect = (index: number) => {
    setSelectedRows((prev) => {
      const newSet = new Set(prev);
      newSet.has(index) ? newSet.delete(index) : newSet.add(index);
      return newSet;
    });
  };

  const resetFormState = () => {
    formZona.resetForm();
    setSelectedZona(null);
    setSelectedRazon(null);
    setSelectedUbicacion(null);
    setSelectedRango(null);
    setSelectedRows(new Set());
    setSortedPaquetes([]);
    setidPackage(0);
  };

  useEffect(() => {
    if (Array.isArray(lista) && lista.length > 0) {
      setSortedPaquetes(lista);
    } else {
      setSortedPaquetes([]);
    }
  }, [lista]);

  return (
    <Modal
      id="kt_modal_create"
      tabIndex={-1}
      aria-hidden="true"
      dialogClassName="modal-dialog modal-dialog-centered modal-xl"
      show={show}
      onHide={() => {
        resetFormState();
        handleClose(false);
      }}
    >
      <div className="modal-content">
        <div className="modal-header pb-0 border-0 justify-content-end">
          <div
            className="btn btn-sm btn-icon btn-active-color-primary"
            data-bs-dismiss="modal"
            onClick={() => {
              resetFormState();
              handleClose(false);
            }}
          >
            <KTSVG
              className="svg-icon-2"
              path="/media/icons/duotune/arrows/arr061.svg"
            />
          </div>
        </div>

        <div className="modal-body pt-0">
          <div className="text-center mb-13">
            <h1 className="mb-3">Devolución múltiple de productos</h1>
            <div className="text-muted fw-semibold fs-5">
              Define los detalles y selecciona las unidades a devolver.
            </div>
          </div>
          <form
            id="kt_modal_generic_form"
            className="mx-auto w-100 mw-800px pt-10 pb-10"
            autoComplete="off"
            onSubmit={formZona.handleSubmit}
          >
            {/* ----------------- Detalles ---------------- */}
            <div className="d-flex flex-stack mb-3">
              <h3 className="text-gray-500">Detalles de devolución</h3>
            </div>
            <div className="separator separator-dashed mb-5" />
            <div className="row g-9 mb-9">
              <div className="col-md-4">
                <label className="required fw-bold fs-6 mb-2">
                  Devuelto de
                </label>
                <Select
                  options={zonas}
                  placeholder={'Seleccione'}
                  onChange={handleZonas}
                  value={selectedZona}
                  className="mb-3"
                  noOptionsMessage={() => {
                    return <span>No hay registros</span>;
                  }}
                  styles={{
                    placeholder: (provided, state) => ({
                      ...provided,
                      color: '#181c32',
                    }),
                    indicatorSeparator: (provided, state) => ({
                      ...provided,
                      display: 'none',
                    }),
                    dropdownIndicator: (provided, state) => ({
                      ...provided,
                      color: '#7E8199',
                    }),
                    control: (provided, state) => ({
                      ...provided,
                      border: '1px solid #e4e6ef',
                      borderRadius: '6px',
                      padding: '3px',
                      fontSize: '1.1rem',
                      boxShadow: 'none',
                      backgroundColor: state.isDisabled ? '#EFF2F5' : 'white',
                    }),
                    option: (provided, state) => ({
                      ...provided,
                      fontSize: '1.1rem',
                    }),
                  }}
                />
                {formZona.touched.zone && formZona.errors.zone && (
                  <div className="text-danger">{formZona.errors.zone}</div>
                )}
              </div>
              <div className="col-md-4">
                <label className="required fw-bold fs-6 mb-2">Razón</label>
                <Select
                  options={razon}
                  placeholder={'Seleccione'}
                  onChange={handleRazon}
                  value={selectedRazon}
                  className="mb-3"
                  noOptionsMessage={() => {
                    return <span>No hay registros</span>;
                  }}
                  styles={{
                    placeholder: (provided, state) => ({
                      ...provided,
                      color: '#181c32',
                    }),
                    indicatorSeparator: (provided, state) => ({
                      ...provided,
                      display: 'none',
                    }),
                    dropdownIndicator: (provided, state) => ({
                      ...provided,
                      color: '#7E8199',
                    }),
                    control: (provided, state) => ({
                      ...provided,
                      border: '1px solid #e4e6ef',
                      borderRadius: '6px',
                      padding: '3px',
                      fontSize: '1.1rem',
                      boxShadow: 'none',
                      backgroundColor: state.isDisabled ? '#EFF2F5' : 'white',
                    }),
                    option: (provided, state) => ({
                      ...provided,
                      fontSize: '1.1rem',
                    }),
                  }}
                />
                {formZona.touched.razon && formZona.errors.razon && (
                  <div className="text-danger">{formZona.errors.razon}</div>
                )}
              </div>
              <div className="col-md-4">
                <label className="fw-bold fs-6 mb-2">Responsable de zona</label>
                <input
                  {...formZona.getFieldProps('responsability_user')}
                  className="form-control mb-3 mb-lg-0"
                  type="text"
                  autoComplete="off"
                  disabled
                />
              </div>
              <div className="col-md-12">
                <label className="required fw-bold fs-6 mb-2">
                  Descripción
                </label>
                <textarea
                  rows={4}
                  style={{ resize: 'none' }}
                  maxLength={500}
                  placeholder="Ej. Falta de unidades"
                  {...formZona.getFieldProps('description')}
                  className="form-control mb-3 mb-lg-0"
                  autoComplete="off"
                />
                {formZona.touched.description &&
                  formZona.errors.description && (
                    <div className="text-danger">
                      {formZona.errors.description}
                    </div>
                  )}
              </div>
            </div>
            {/* ----------------- Unidades ---------------- */}
            <div className="mb-5">
              <div className="d-flex flex-stack mb-3">
                <h3 className="text-gray-500">Unidades a devolver</h3>
              </div>
              <div className="separator separator-dashed mb-5" />

              <div className="d-flex justify-content-between align-items-center flex-wrap gap-4">
                <div className="d-flex flex-wrap gap-4 align-items-end">
                  {/* --- Rango con contador al lado derecho --- */}
                  <div
                    className="d-flex flex-column"
                    style={{ minWidth: '250px' }}
                  >
                    <label className="fw-bold fs-6 mb-2 d-block">Rango</label>

                    {/* Contenedor en línea: Select + texto */}
                    <div className="d-flex align-items-center gap-3">
                      <Select
                        options={rango}
                        placeholder="Rango"
                        onChange={handleRango}
                        value={selectedRango}
                        noOptionsMessage={() => <span>No hay registros</span>}
                        menuPlacement="auto"
                        menuPosition="fixed"
                        menuPortalTarget={document.body}
                        styles={{
                          control: (provided, state) => ({
                            ...provided,
                            border: '1px solid #e4e6ef',
                            borderRadius: '6px',
                            fontSize: '1.1rem',
                            boxShadow: 'none',
                            backgroundColor: state.isDisabled
                              ? '#EFF2F5'
                              : 'white',
                          }),
                          menuPortal: (base) => ({
                            ...base,
                            zIndex: 9999,
                          }),
                        }}
                      />

                      {selectedRows.size > 0 && (
                        <small className="text-gray-600">
                          {`${selectedRows.size} ${
                            selectedRows.size === 1
                              ? 'seleccionado'
                              : 'seleccionados'
                          }`}
                        </small>
                      )}
                    </div>
                  </div>
                </div>

                {/* ---- Botones (alineados al centro verticalmente) ---- */}
                <div className="d-flex flex-column align-items-start">
                  <label className="fw-bold fs-6 mb-2 d-block">
                    Total Registros: {lista.length}
                  </label>

                  <div className="d-flex gap-3 align-items-center">
                    <button
                      type="button"
                      className="btn btn-light d-flex justify-content-center align-items-center rounded"
                      style={{ width: '40px', height: '40px' }}
                      onClick={() => {
                        setidPackage((prev) => prev);
                        setSelectedRango(null);
                        setSelectedRows(new Set());
                        setSortedPaquetes([]);
                      }}
                    >
                      <KTSVG
                        path="/media/icons/duotune/arrows/arr029.svg"
                        svgClassName="w-20px h-20px"
                      />
                    </button>

                    <button
                      type="button"
                      className="btn btn-primary d-flex justify-content-center align-items-center rounded"
                      style={{ width: '40px', height: '40px' }}
                      onClick={() => {
                        if (!lista || lista.length === 0) {
                          SimpleAlert(
                            'No hay datos para ordenar',
                            2000,
                            'warning'
                          );
                          return;
                        }

                        const sorted = [...lista].sort((a, b) =>
                          isAsc ? a.count - b.count : b.count - a.count
                        );

                        setSortedPaquetes(sorted);
                        setIsAsc(!isAsc);

                        SimpleAlert(
                          isAsc
                            ? 'Lista ordenada de menor a mayor'
                            : 'Lista ordenada de mayor a menor',
                          2000,
                          'info'
                        );
                      }}
                    >
                      <KTSVG
                        path="/media/icons/duotune/arrows/arr093.svg"
                        svgClassName="w-20px h-20px"
                      />
                    </button>
                  </div>
                </div>
              </div>
              <div style={{ height: '20px' }}>
                {formZona.touched.ubicacion && formZona.errors.ubicacion && (
                  <div className="text-danger small">
                    {formZona.errors.ubicacion}
                  </div>
                )}
              </div>
              {/* ----------------- Tabla ---------------- */}
              <div className="mt-5 py-3">
                <div
                  className="table-responsive"
                  style={{ maxHeight: '500px', overflowY: 'auto' }}
                >
                  <table className="table align-middle gs-0 gy-4">
                    <thead
                      style={{
                        position: 'sticky',
                        top: 0,
                        background: '#f9f9f9',
                        zIndex: 2,
                      }}
                    >
                      <tr className="fw-bold text-muted bg-light">
                        <th className="min-w-50px">
                          <input
                            className="form-check-input ms-3"
                            type="checkbox"
                            checked={isAllSelected}
                            onChange={toggleSelectAll}
                          />
                        </th>
                        <th className="min-w-100px">#</th>
                        <th className="min-w-125px">ICCID</th>
                        <th className="min-w-125px">Número</th>
                        <th className="text-start">Ubicación</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(sortedPaquetes.length > 0 ? sortedPaquetes : lista)
                        ?.length > 0 ? (
                        (sortedPaquetes.length > 0
                          ? sortedPaquetes
                          : lista
                        ).map((item: any, index: number) => (
                          <tr key={index}>
                            <td>
                              <input
                                className="form-check-input ms-3"
                                type="checkbox"
                                checked={selectedRows.has(index)}
                                onChange={() => toggleSelect(index)}
                              />
                            </td>
                            <td>
                              <div className="d-flex align-items-center">
                                <div className="d-flex justify-content-start flex-column">
                                  <span className="text-gray-900 fw-bold text-hover-primary mb-1 fs-6">
                                    {item.count}
                                  </span>
                                </div>
                              </div>
                            </td>
                            <td>
                              <span className="text-gray-900 fw-bold text-hover-primary d-block mb-1 fs-6">
                                {item.iccid}
                              </span>
                            </td>
                            <td>
                              <span className="text-gray-900 fw-bold text-hover-primary d-block mb-1 fs-6">
                                {item.numero}
                              </span>
                            </td>
                            <td>
                              <span className="fw-semibold text-gray-900">
                                {item.ubicacion}
                              </span>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td
                            colSpan={5}
                            className="text-center py-5 text-muted"
                          >
                            No hay unidades disponibles
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
              {/* ----------------- Fin tabla ---------------- */}
            </div>
            <div className="text-center pt-10">
              <button
                type="reset"
                className="btn btn-light me-3"
                onClick={() => {
                  resetFormState();
                  handleClose(false);
                }}
              >
                Cancelar
              </button>

              <button
                type="button"
                className="btn btn-primary"
                disabled={loading}
                onClick={async () => {
                  const errors = await formZona.validateForm();
                  const hasErrors = Object.keys(errors).length > 0;

                  if (hasErrors) {
                    SimpleAlert(
                      'Algunos datos no han sido agregados. Por favor, asegúrese de que toda la información solicitada haya sido capturada.',
                      4000,
                      'error'
                    );
                    formZona.setTouched(
                      Object.keys(errors).reduce(
                        (acc, key) => ({ ...acc, [key]: true }),
                        {}
                      ),
                      true
                    );
                    return;
                  }

                  formZona.handleSubmit();
                }}
              >
                {!loading && <span className="indicator-label">Confirmar</span>}
                {loading && (
                  <span
                    className="indicator-progress"
                    style={{ display: 'block' }}
                  >
                    Espere por favor...
                    <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
                  </span>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </Modal>
  );
};

export { CreateModal };
